/* eslint-disable max-lines */
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import useFetch from "../../../hooks/useFetch";
import {
  getEsimCompetitorComparison,
  getEsimLikeForLike,
  getEsimMarginCountries,
  getEsimMarginTariff,
  getEsimRealizedMargins
} from "../../../services/EsimMargin.service";
import { eurOrDash, eurPerGb, isNotFound } from "../../../utils/esim/margin";
import DashboardCard from "../../dashboard/DashboardCard";
import DayPicker from "../../ui/datepicker/day/DayPicker";
import RangePicker from "../../ui/datepicker/range/RangePicker";
import InfoTip, { WithTip } from "../../ui/InfoTip";
import { SpinLoader } from "../../ui/SpinLoader";
import LikeForLikeTable from "./LikeForLikeTable";
import { LoadFailed, NoSnapshot, SnapshotJob } from "./MarginStates";
import PriceCurve from "./PriceCurve";
import RealizedTable from "./RealizedTable";
import TariffTable from "./TariffTable";

type Tab = "tariff" | "realized" | "competitors";

// EsimMarginController.MaxRealizedRangeDays: the realized margins join each sale to its hourly usage.
const MAX_REALIZED_DAYS = 366;

const TABS: { key: Tab; label: string }[] = [
  { key: "tariff", label: "Tarifa e operatorit" },
  { key: "realized", label: "Fitimi real" },
  { key: "competitors", label: "Konkurrenca" }
];

// The regions we sell compare as destinations of their own; the codes are the snapshot's.
const REGION_OPTIONS = [
  { value: "EU", label: "Europë (rajon)" },
  { value: "XB", label: "Ballkan (rajon)" },
  { value: "WW", label: "Global (rajon)" }
];

// One shared empty list: a fresh [] on every render would reset the tables' paging each time.
const NONE: never[] = [];

const HOW_IT_IS_COUNTED =
  "Operatori nuk na faturon për paketë, por për çdo GB që klienti përdor, me çmimin e rrjetit ku lidhet. " +
  "Tarifa tregon rastin më të keq: sa GB mund të përdorë klienti para se paketa " +
  "të na kushtojë më shumë se çmimi i saj. Fitimi real tregon çfarë ndodhi vërtet: shitjet e një periudhe " +
  "kundrejt kostos që operatori na faturoi për përdorimin e atyre paketave.";

const COMPETITOR_SNAPSHOT = "Listat publike të çmimeve të konkurrentëve, të lexuara në këtë datë.";

const snapshotDay = (date: string) => dayjs(date).format("DD/MM/YYYY");

const EsimMargin: FC = () => {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [tab, setTab] = useState<Tab>("competitors");
  const [countryIso2, setCountryIso2] = useState("");
  const [from, setFrom] = useState(dayjs().subtract(30, "day").format("YYYY-MM-DD"));
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DD"));
  const [basketCountry, setBasketCountry] = useState("");

  const dateParam = date ? dayjs(date).format("YYYY-MM-DD") : undefined;

  // resetResponseOnError: a day with no snapshot must not leave another day's figures on screen.
  const countries = useFetch(getEsimMarginCountries, { loading: true, resetResponseOnError: true });
  const tariff = useFetch(getEsimMarginTariff, { resetResponseOnError: true });
  const realized = useFetch(getEsimRealizedMargins, { resetResponseOnError: true });
  const competitorDetail = useFetch(getEsimCompetitorComparison, { resetResponseOnError: true });
  const likeForLike = useFetch(getEsimLikeForLike, { resetResponseOnError: true });

  // What each fetch last asked for, so coming back to a tab does not ask the same again; a failed
  // answer is asked again, and the retry links call handleFunc themselves.
  const lastAsked = useRef<Record<string, string>>({});
  const askOnce = <P extends object>(
    name: string,
    fetch: { error: unknown; handleFunc: (p: P) => unknown },
    params: P
  ) => {
    const key = JSON.stringify(params);
    if (lastAsked.current[name] === key && !fetch.error) return;
    lastAsked.current[name] = key;
    void fetch.handleFunc(params);
  };

  // The countries feed the summary cards, so they load for every day; the other tabs load when opened.
  useEffect(() => {
    void countries.handleFunc({ date: dateParam });
  }, [dateParam]);

  useEffect(() => {
    if (tab === "tariff") askOnce("tariff", tariff, { date: dateParam, countryIso2 });
    if (tab === "realized") askOnce("realized", realized, { from, to });
  }, [tab, dateParam, countryIso2, from, to]);

  // The competitors tab is one destination at a time: the basket table and, under it, the price
  // curve, which needs both sides' plans from the comparison of that destination.
  useEffect(() => {
    if (tab !== "competitors" || !basketCountry) return;
    askOnce("likeForLike", likeForLike, { date: dateParam, countryIso2: basketCountry });
    askOnce("competitorDetail", competitorDetail, { date: dateParam, countryIso2: basketCountry });
  }, [tab, dateParam, basketCountry]);

  const summary = countries.response?.data;
  const totals = useMemo(() => {
    const rows = summary?.countries ?? [];
    const priced = rows.filter((c) => c.tariffMaxCostPerGb !== null);
    const dearest = priced.reduce<typeof rows[number] | null>(
      (best, c) => (best === null || (c.tariffMaxCostPerGb ?? 0) > (best.tariffMaxCostPerGb ?? 0) ? c : best),
      null
    );
    const cheapest = priced.reduce<typeof rows[number] | null>(
      (best, c) => (best === null || (c.tariffMinCostPerGb ?? 0) < (best.tariffMinCostPerGb ?? 0) ? c : best),
      null
    );
    return {
      countries: rows.length,
      covered: rows.filter((c) => c.packageCount > 0).length,
      losingPackages: rows.reduce((sum, c) => sum + c.packagesLosingMoney, 0),
      packages: rows.reduce((sum, c) => sum + c.packageCount, 0),
      dearest,
      cheapest
    };
  }, [summary]);

  const countryOptions = useMemo(
    () =>
      [...(summary?.countries ?? [])]
        .filter((c) => c.packageCount > 0)
        .sort((a, b) => (a.countryName ?? a.countryIso2).localeCompare(b.countryName ?? b.countryIso2))
        .map((c) => ({
          value: c.countryIso2,
          label: `${c.countryName ?? c.countryIso2} (${c.countryIso2})`
        })),
    [summary]
  );

  const basketOptions = useMemo(() => [...REGION_OPTIONS, ...countryOptions], [countryOptions]);

  // Albania by default, so wait for the countries: the regions alone would pick Europe for good.
  useEffect(() => {
    if (tab !== "competitors" || basketCountry || countries.loading || basketOptions.length === 0) return;
    setBasketCountry(basketOptions.some((o) => o.value === "AL") ? "AL" : basketOptions[0].value);
  }, [tab, basketCountry, basketOptions, countries.loading]);

  const stateOf = (fetch: { error: unknown; loading: boolean }, retry: () => void, job: SnapshotJob) => {
    if (!fetch.error || fetch.loading) return null;
    return isNotFound(fetch.error) ? <NoSnapshot dated={!!date} job={job} /> : <LoadFailed onRetry={retry} />;
  };

  const competitorParams = { date: dateParam, countryIso2: basketCountry };
  // No destination yet means the countries are still loading, not that the destination has no packages.
  const competitorsLoading = !basketCountry || likeForLike.loading || competitorDetail.loading;

  const r = realized.response?.data;
  // The card colours the share by its sign and appends the % itself; no share when nothing was sold.
  const realizedPct =
    r?.marginPercent === null || r?.marginPercent === undefined ? undefined : String(r.marginPercent);

  const basketReport = likeForLike.response?.data;
  const basketLabel = basketOptions.find((o) => o.value === basketCountry)?.label ?? basketCountry;

  return (
    <div className="px-5 pb-8">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold">Marzhet e eSIM</h2>
        <InfoTip text={HOW_IT_IS_COUNTED} />
        {/* The day the figures on screen were read; what each tab compares is on its column titles. */}
        {tab === "tariff" && summary && (
          <span className="ml-2 text-sm text-neutral">Snapshot: {snapshotDay(summary.snapshotDate)}</span>
        )}
        {tab === "competitors" && basketReport && (
          <span className="ml-2 text-sm text-neutral">
            <WithTip
              label={`Snapshot: ${snapshotDay(basketReport.snapshotDate)}`}
              tip={COMPETITOR_SNAPSHOT}
            />
          </span>
        )}
      </div>

      {tab === "tariff" && stateOf(countries, () => void countries.handleFunc({ date: dateParam }), "tariff")}

      {/* Without the countries the cards would read zero; the box above says why instead. */}
      {tab === "tariff" && !countries.error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <DashboardCard
            title="Shtete në tarifë"
            info="Shtetet që operatori i çmon; në kllapa sa prej tyre mbulohen nga një paketë e jona."
            currentValue={`${totals.countries} (${totals.covered} me paketa)`}
            ratePercentageDesc=""
            delay={100}
            loading={countries.loading}
            currency={false}
          />
          <DashboardCard
            title="Paketa që humbin nëse përdoren plotësisht"
            info={
              "Çifte paketë-shtet ku kostoja e gjithë sasisë në rrjetin më të shtrenjtë kalon çmimin. " +
              "E njëjta paketë numërohet në çdo shtet të zonës së saj."
            }
            currentValue={`${totals.losingPackages} nga ${totals.packages}`}
            ratePercentageDesc=""
            delay={200}
            loading={countries.loading}
            currency={false}
          />
          <DashboardCard
            title={`Më i shtrenjti: ${totals.dearest?.countryName ?? totals.dearest?.countryIso2 ?? "–"}`}
            info="Shteti me koston më të lartë për GB në tarifën e operatorit."
            currentValue={eurPerGb(totals.dearest?.tariffMaxCostPerGb)}
            ratePercentageDesc=""
            delay={300}
            loading={countries.loading}
            currency={false}
          />
          <DashboardCard
            title={`Më i liri: ${totals.cheapest?.countryName ?? totals.cheapest?.countryIso2 ?? "–"}`}
            info="Shteti me koston më të ulët për GB në tarifën e operatorit."
            currentValue={eurPerGb(totals.cheapest?.tariffMinCostPerGb)}
            ratePercentageDesc=""
            delay={400}
            loading={countries.loading}
            currency={false}
          />
        </div>
      )}

      {tab === "realized" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <DashboardCard
            title="Shitje"
            info="Çmimi bruto i paketave të konfirmuara në periudhë; dhuratat hyjnë me zero."
            currentValue={eurOrDash(r?.sales)}
            ratePercentageDesc=""
            delay={100}
            loading={realized.loading}
            currency={false}
          />
          <DashboardCard
            title="Kosto e përdorimit"
            info="Çfarë na faturoi operatori për GB-të e përdorura brenda vlefshmërisë së atyre paketave."
            currentValue={eurOrDash(r?.usageCost)}
            ratePercentageDesc=""
            delay={200}
            loading={realized.loading}
            currency={false}
          />
          <DashboardCard
            title="Fitimi"
            info="Shitje minus kosto e përdorimit."
            currentValue={eurOrDash(r?.margin)}
            ratePercentage={realizedPct}
            ratePercentageDesc="e shitjeve"
            delay={300}
            loading={realized.loading}
            currency={false}
          />
          <DashboardCard
            title="Paketa të shitura"
            info="Në kllapa sa prej tyre ishin dhurata."
            currentValue={`${r?.sold ?? 0} (${r?.giftCount ?? 0} falas)`}
            ratePercentageDesc=""
            delay={400}
            loading={realized.loading}
            currency={false}
          />
        </div>
      )}

      <div
        className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4 relative z-30"
      >
        <div className="flex gap-1 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={
                tab === t.key
                  ? "px-4 py-2 text-sm font-medium text-primary border-b-2 border-primary -mb-px"
                  : "px-4 py-2 text-sm font-medium text-neutral hover:text-black"
              }
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {tab === "tariff" && (
            <div className="flex flex-col sm:flex-row sm:items-center">
              <label
                htmlFor="esim-margin-country"
                className="mb-2 sm:mb-0 sm:mr-2 text-sm font-medium text-gray-700 whitespace-nowrap"
              >
                Shteti:
              </label>
              <select
                id="esim-margin-country"
                value={countryIso2}
                onChange={(e) => setCountryIso2(e.target.value)}
                className="w-full sm:w-56 px-3 py-2 border border-gray-300 rounded-md shadow-sm
                  focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-sm"
              >
                <option value="">Të gjitha</option>
                {countryOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {tab === "competitors" && (
            <div className="flex flex-col sm:flex-row sm:items-center">
              <label
                htmlFor="esim-basket-country"
                className="mb-2 sm:mb-0 sm:mr-2 text-sm font-medium text-gray-700 whitespace-nowrap"
              >
                Destinacioni:
              </label>
              <select
                id="esim-basket-country"
                value={basketCountry}
                onChange={(e) => setBasketCountry(e.target.value)}
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm
                  focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-sm"
              >
                {basketOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {tab === "realized" ? (
            <div className="flex flex-col sm:flex-row sm:items-center">
              <label className="mb-2 sm:mb-0 sm:mr-2 text-sm font-medium text-gray-700 whitespace-nowrap">
                Blerë nga:
              </label>
              <div className="w-full sm:w-64">
                <RangePicker
                  className="flex flex-row items-center w-full"
                  range={{ from: dayjs(from).toDate(), to: dayjs(to).toDate() }}
                  pickerProps={{ toDate: dayjs().add(1, "day").toDate() }}
                  hasHelpers={true}
                  onApply={(range) => {
                    if (!range?.from) return;
                    // A half-picked range is a single day, not an open end.
                    const end = dayjs(range.to ?? range.from);
                    // The backend answers at most MAX_REALIZED_DAYS, both ends included;
                    // a longer pick keeps its end.
                    const earliest = end.subtract(MAX_REALIZED_DAYS - 1, "day");
                    const start = dayjs(range.from).isBefore(earliest) ? earliest : dayjs(range.from);
                    setFrom(start.format("YYYY-MM-DD"));
                    setTo(end.format("YYYY-MM-DD"));
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center">
              <label className="mb-2 sm:mb-0 sm:mr-2 text-sm font-medium text-gray-700 whitespace-nowrap">
                Data:
              </label>
              <div className="w-full sm:w-52">
                <DayPicker
                  date={date}
                  placeholder="Snapshot-i më i fundit"
                  pickerProps={{ toDate: new Date() }}
                  onChangeDate={(d) => setDate(d)}
                />
              </div>
              {date && (
                <button
                  type="button"
                  className="sm:ml-2 text-sm text-neutral underline whitespace-nowrap"
                  onClick={() => setDate(undefined)}
                >
                  Më i fundit
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {tab === "tariff" && (
        <>
          {/* The countries' box already says there is no snapshot; one is enough. */}
          {!(isNotFound(countries.error) && isNotFound(tariff.error)) &&
            stateOf(tariff, () => void tariff.handleFunc({ date: dateParam, countryIso2 }), "tariff")}
          {!tariff.error && (
            <TariffTable rates={tariff.response?.data?.rates ?? NONE} loading={tariff.loading} />
          )}
        </>
      )}
      {tab === "realized" && (
        <>
          {!!realized.error && !realized.loading && (
            <LoadFailed onRetry={() => void realized.handleFunc({ from, to })} />
          )}
          {!realized.error && <RealizedTable packages={r?.packages ?? NONE} loading={realized.loading} />}
        </>
      )}
      {tab === "competitors" && (
        <>
          {stateOf(likeForLike, () => void likeForLike.handleFunc(competitorParams), "competitors")}
          {/* While a destination loads, the last one's rows and curve would sit under the new name. */}
          {competitorsLoading && (
            <div className="flex justify-center py-16">
              <SpinLoader formatClassName="h-8 w-8" colorText="text-primary" />
            </div>
          )}
          {!competitorsLoading && !likeForLike.error && (
            <>
              <LikeForLikeTable report={basketReport ?? null} loading={false} />
              <div className="mt-8">
                {stateOf(
                  competitorDetail,
                  () => void competitorDetail.handleFunc(competitorParams),
                  "competitors"
                )}
                {competitorDetail.response?.data && (
                  <PriceCurve detail={competitorDetail.response.data} countryName={basketLabel} />
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default EsimMargin;
